import { Fragment } from 'react';
import { brandConfig } from '../../config/brands';
import { usePartnerBranding } from '../../hooks/usePartnerBranding';
import { useSupportMeeting } from '../../features/meetings/hooks';
import { isDividerKey, MENU_ITEM_PATHS, type MenuToken } from '../../config/menuOrder';
import { hardNavigate } from '../../utils/navigation';
import { usePageOverlayStore } from '../../stores/pageOverlayStore';

type MenuContentProps = {
  isAdmin: boolean;
  isLoggedIn: boolean;
  onLogout: () => void;
  /** ナビリンク（素材・商品データ）を表示するか */
  showNavLinks?: boolean;
  /** ポイント残高（モバイルメニュー用） */
  creditsBalance?: number | null;
};

/** 外部リンク項目用のアイコン (矢印つきウィンドウ) */
function ExternalLinkIcon() {
  return (
    <svg
      className="menu-item-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/**
 * メニュードロップダウンの内容コンポーネント
 * PC用ヘッダーとモバイル用フッターで共有
 */
export function MenuContent({ isAdmin, isLoggedIn, onLogout, showNavLinks = false, creditsBalance }: MenuContentProps) {
  const { menuOrder } = usePartnerBranding(isLoggedIn);
  // minimalUserMenu (usodeki 等) は「利用状況・マイページ・お問い合わせ・ログアウト」のみに絞る。
  // 公開ホーム (未ログイン閲覧) では認証必須ページへの項目を出さず「ログイン」を出す
  const minimalMenu = brandConfig.features.minimalUserMenu ?? false;
  const overlayNav = brandConfig.features.overlayPageNavigation ?? false;
  const visibleTokens: MenuToken[] = !isLoggedIn ? [] : minimalMenu ? ['dashboard', 'profile'] : menuOrder;
  const openOverlay = usePageOverlayStore((s) => s.open);
  // 1 画面構成ブランドは利用状況/マイページをフルリロード遷移せずオーバーレイで開く
  // (戻る導線確保)。それ以外のブランドは従来どおりハードナビゲート。
  const goDashboard = () => (overlayNav ? openOverlay('dashboard') : hardNavigate(MENU_ITEM_PATHS.dashboard));
  const goProfile = () => (overlayNav ? openOverlay('profile') : hardNavigate(MENU_ITEM_PATHS.profile));
  // ミーティング枠を持つ法人プランの組織でのみ「ミーティング」項目を表示する。
  // 判定はプラン依存の動的なものなので menuOrder トークンには含めず別管理。
  // (最小メニューでは項目自体を出さないため取得もしない)
  const { data: supportMeeting } = useSupportMeeting(isLoggedIn && !minimalMenu);
  // ブランド設定の外部リンク (姉妹サービス等)。未設定なら何も出さない
  const externalLinks = brandConfig.menuExternalLinks ?? [];

  const renderItem = (token: MenuToken) => {
    if (isDividerKey(token)) {
      return <div className="menu-divider" />;
    }
    if (token === 'dashboard') {
      return (
        <button type="button" onClick={goDashboard} className="menu-item-with-icon">
          <img src="/analytics_board_graph_line.svg" alt="" className="menu-item-icon" />
          <span className={creditsBalance != null ? 'menu-item-label menu-item-label--stacked' : 'menu-item-label'}>
            利用状況
            {creditsBalance != null && (
              <span className="mobile-menu-points-sub">
                <span className="mobile-menu-points-number">{Math.floor(creditsBalance).toLocaleString()}</span>
                <span className="mobile-menu-points-unit">ポイント</span>
              </span>
            )}
          </span>
        </button>
      );
    }
    if (token === 'tags') {
      return (
        <button type="button" onClick={() => hardNavigate(MENU_ITEM_PATHS.tags)} className="menu-item-with-icon">
          <img src="/tags_1.svg" alt="" className="menu-item-icon" />
          {brandConfig.labels.tagManagement}
        </button>
      );
    }
    if (token === 'brands') {
      return (
        <button type="button" onClick={() => hardNavigate(MENU_ITEM_PATHS.brands)} className="menu-item-with-icon">
          <img src="/sign_badge_badge_1.svg" alt="" className="menu-item-icon" />
          ブランド管理
        </button>
      );
    }
    const labels: Record<Exclude<typeof token, 'dashboard' | 'tags' | 'brands'>, string> = {
      subscription: 'プラン・契約',
      organizations: '組織情報',
      profile: 'マイページ',
    };
    const icons: Record<Exclude<typeof token, 'dashboard' | 'tags' | 'brands'>, string> = {
      subscription: '/money_wallet_open.svg',
      organizations: '/office_building_double.svg',
      profile: '/single_neutral_actions_edit_1.svg',
    };
    return (
      <button
        type="button"
        onClick={token === 'profile' ? goProfile : () => hardNavigate(MENU_ITEM_PATHS[token])}
        className="menu-item-with-icon"
      >
        <img src={icons[token]} alt="" className="menu-item-icon" />
        {labels[token]}
      </button>
    );
  };

  return (
    <>
      {showNavLinks && brandConfig.features.mainNavTabs && (
        <>
          <button type="button" onClick={() => hardNavigate('/studio')}>{brandConfig.labels.studio}</button>
          <button type="button" onClick={() => hardNavigate('/products')}>{brandConfig.labels.products}</button>
          <button type="button" onClick={() => hardNavigate('/exclusive-actors')}>{brandConfig.labels.aiActors}</button>
          <div className="menu-divider"></div>
        </>
      )}
      {visibleTokens.map((token, index) => (
        <Fragment key={`${token}-${index}`}>{renderItem(token)}</Fragment>
      ))}
      {externalLinks.length > 0 && (
        <>
          <div className="menu-divider"></div>
          {externalLinks.map((link) => (
            <button
              key={link.url}
              type="button"
              onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
              className="menu-item-with-icon"
            >
              <ExternalLinkIcon />
              {link.label}
            </button>
          ))}
        </>
      )}
      {!minimalMenu && supportMeeting?.eligible && (
        <button type="button" onClick={() => hardNavigate('/support-meeting')} className="menu-item-with-icon">
          <img src="/video_call.svg" alt="" className="menu-item-icon" />
          ミーティング
        </button>
      )}
      {!minimalMenu && isAdmin && (
        <>
          <div className="menu-divider"></div>
          {/* 接続管理は Amazon に依存しない (Google 広告等、ブランド非依存の連携も含む) */}
          <button type="button" onClick={() => hardNavigate('/connections')} className="menu-item-with-icon">
            <img src="/charger_1.svg" alt="" className="menu-item-icon" />
            接続管理
          </button>
          <button type="button" onClick={() => hardNavigate('/users')} className="menu-item-with-icon">
            <img src="/multiple_neutral_2.svg" alt="" className="menu-item-icon" />
            ユーザー管理
          </button>
          <button type="button" onClick={() => hardNavigate('/organizations/join-requests')} className="menu-item-with-icon">
            <img src="/single_neutral_actions_check_1.svg" alt="" className="menu-item-icon" />
            参加依頼管理
          </button>
        </>
      )}
      <div className="menu-divider"></div>
      <button type="button" onClick={() => window.open(brandConfig.urls.contact, '_blank')} className="menu-item-with-icon">
        <img src="/send_email_envelope.svg" alt="" className="menu-item-icon" />
        お問い合わせ
      </button>
      {isLoggedIn ? (
        <button type="button" onClick={onLogout} className="menu-item-with-icon">
          <img src="/following_1.svg" alt="" className="menu-item-icon" />
          ログアウト
        </button>
      ) : (
        // 公開ホーム (未ログイン閲覧) 用。AuthGate は SPA 遷移を再評価しないため hardNavigate
        <button type="button" onClick={() => hardNavigate('/login')} className="menu-item-with-icon">
          <img src="/following_1.svg" alt="" className="menu-item-icon" />
          ログイン
        </button>
      )}
    </>
  );
}
